import React, { useMemo } from 'react';
import BrandingFooter from '../common/BrandingFooter';
import TeamBadge, { pickTeamLogo } from '../common/TeamBadge';
import MatchWidgetShell from '../common/MatchWidgetShell';
import MatchScoreHero from '../common/MatchScoreHero';
import SubstitutionPanel, { splitSubstitutionsBySide } from '../common/SubstitutionPanel';

function LineupPanel({ teamName, logoUrl, data }) {
  if (!data?.players?.length) {
    return (
      <div className="sw-lineup-panel">
        <div className="sw-lineup-panel__head flex items-center gap-2">
          <TeamBadge name={teamName} logoUrl={logoUrl} size={22} />
          {teamName}
        </div>
        <p className="text-xs text-center py-8 px-4" style={{ color: 'var(--sw-muted)' }}>
          Lineup not available
        </p>
      </div>
    );
  }

  return (
    <div className="sw-lineup-panel">
      <div className="sw-lineup-panel__head flex items-center gap-2">
        <TeamBadge name={teamName} logoUrl={logoUrl} size={22} />
        <span className="truncate flex-1">{teamName}</span>
        {data.formation && <span className="sw-formation-pill">{data.formation}</span>}
      </div>
      <ul>
        {data.players.map((p, i) => (
          <li key={`${p.number}-${p.name}-${i}`} className="sw-lineup-player">
            <span className="sw-lineup-number">{p.number || '—'}</span>
            <span className="font-semibold flex-1 truncate">{p.name}</span>
            {p.booking && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                {p.booking}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Merge explicit subs with bench players from lineup XML. */
function buildSideSubs(apiSubs, benchPlayers = [], side = 'home') {
  const rows = apiSubs.map((s) => ({ ...s, side }));
  for (const p of benchPlayers) {
    if (!p.name) continue;
    const alreadyListed = rows.some((s) => s.in === p.name || s.out === p.name);
    if (!alreadyListed) {
      rows.push({
        side,
        minute: '',
        in: p.name,
        out: '',
        booking: p.booking || '',
      });
    }
  }
  return rows;
}

export default function Lineups({
  showBranding = false,
  competitionLabel = 'Premier League',
  featuredMatch = null,
  lineups = null,
  substitutions = [],
}) {
  const m = featuredMatch || {};
  const data = lineups || m.lineups;

  const { home: homeApi, away: awayApi } = splitSubstitutionsBySide(substitutions);

  const homeSubs = useMemo(
    () => buildSideSubs(homeApi, data?.home?.bench || [], 'home'),
    [homeApi, data?.home?.bench]
  );

  const awaySubs = useMemo(
    () => buildSideSubs(awayApi, data?.away?.bench || [], 'away'),
    [awayApi, data?.away?.bench]
  );

  const hasSubs = homeSubs.length > 0 || awaySubs.length > 0;

  return (
    <div>
      <MatchWidgetShell competitionLabel={competitionLabel} subtitle="Starting XI & formations">
        <MatchScoreHero match={m} compact />

        <div className="px-4 pt-2">
          <p className="sw-section-title mb-3">Starting XI</p>
        </div>
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <LineupPanel
            teamName={m.home || 'Home'}
            logoUrl={pickTeamLogo(m, 'home')}
            data={data?.home}
          />
          <LineupPanel
            teamName={m.away || 'Away'}
            logoUrl={pickTeamLogo(m, 'away')}
            data={data?.away}
          />
        </div>

        {hasSubs && (
          <>
            <div className="px-4">
              <p className="sw-section-title mb-3">Substitutions</p>
            </div>
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SubstitutionPanel
                teamName={m.home || 'Home'}
                logoUrl={pickTeamLogo(m, 'home')}
                subs={homeSubs}
              />
              <SubstitutionPanel
                teamName={m.away || 'Away'}
                logoUrl={pickTeamLogo(m, 'away')}
                subs={awaySubs}
              />
            </div>
          </>
        )}

        {!data?.home?.players?.length && !data?.away?.players?.length && (
          <p className="text-center text-xs pb-8 px-4" style={{ color: 'var(--sw-muted)' }}>
            Lineups will appear when the feed publishes team sheets.
          </p>
        )}
      </MatchWidgetShell>
      {showBranding && <BrandingFooter />}
    </div>
  );
}
